import React from 'react';
import { Trash2, Reply, Pencil, Ban } from 'lucide-react';
import RichContentBlocks from '../RichContentBlocks';
import { Avatar } from './Avatar';
import { QuoteBlock } from './QuoteBlock';
import { fullDate, monthYear } from './forumUtils';
import { normalizeHtml } from '../../lib/htmlText';

// Classic forum post: author column on the left, content on the right.
export function PostBubble({ post, isFirst, index, canDelete, onDelete, onReply, canEdit, onEdit, canBlock, onBlock }) {
  return (
    <article
      id={`post-${post.id}`}
      data-testid={`forum-post-${post.id}`}
      className={`scroll-mt-24 transition-all flex flex-col sm:flex-row bg-white border rounded-2xl overflow-hidden shadow-sm ${isFirst ? 'border-cmic-200 ring-1 ring-cmic-100' : 'border-slate-200'}`}
    >
      {/* Author column */}
      <div className="sm:w-48 flex-shrink-0 bg-slate-50/80 border-b sm:border-b-0 sm:border-r border-slate-100 p-4 flex sm:flex-col items-center sm:text-center gap-3">
        <Avatar name={post.author_name} photoUrl={post.author_photo_url} size={64} />
        <div className="min-w-0 flex-1 sm:flex-none">
          <div className="font-semibold text-cmic-700 leading-tight break-words">{post.author_name}</div>
          {post.author_title ? (
            <div className="mt-1 inline-flex items-center justify-center text-[10px] font-bold uppercase tracking-wide bg-cmic-100 text-cmic-700 border border-cmic-200 px-2 py-0.5 rounded-full" data-testid="forum-post-author-title">
              {post.author_title}
            </div>
          ) : (
            <div className="text-[11px] text-slate-400 mt-0.5">
              {post.author_role === 'admin' ? 'Administrador' : 'Miembro'}
            </div>
          )}
          <dl className="mt-3 pt-3 border-t border-slate-200/70 hidden sm:block text-[11px] text-slate-400 space-y-1">
            {post.author_joined_at && (
              <div>Desde <span className="text-slate-600 font-medium block">{monthYear(post.author_joined_at)}</span></div>
            )}
            <div>Mensajes <span className="text-slate-600 font-medium">{post.author_message_count ?? 0}</span></div>
          </dl>
        </div>
      </div>

      {/* Content column */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-100 text-xs text-slate-400">
          <span>
            {fullDate(post.created_at)}
            {post.edited_at && (
              <span className="ml-2 italic text-slate-400" data-testid={`forum-post-edited-${post.id}`}>
                · editado{post.edited_by ? ` por ${post.edited_by}` : ''}{post.edited_by_mod ? ' (moderador)' : ''} · {fullDate(post.edited_at)}
              </span>
            )}
          </span>
          <span className="font-semibold text-slate-300">#{index}</span>
        </div>
        <div className="p-5 flex-1 text-[15px] text-slate-700 leading-relaxed">
          {post.quote && <QuoteBlock quote={post.quote} />}
          {post.body_html
            ? <div className="forum-rich-content break-words" dangerouslySetInnerHTML={{ __html: normalizeHtml(post.body_html) }} />
            : (post.body && <div className="whitespace-pre-wrap break-words mb-2">{post.body}</div>)}
          <RichContentBlocks blocks={post.body_blocks} />
        </div>
        <div className="px-5 py-2 border-t border-slate-100 flex items-center justify-end gap-1 flex-wrap">
          {canBlock && (
            <button onClick={() => onBlock(post)} title="Bloquear autor del foro" data-testid={`forum-post-block-${post.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-orange-600 px-2.5 py-1.5 rounded-lg hover:bg-orange-50 transition-colors">
              <Ban size={13} /> Bloquear
            </button>
          )}
          {canEdit && (
            <button onClick={() => onEdit(post)} title="Editar" data-testid={`forum-post-edit-${post.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-cmic-600 px-2.5 py-1.5 rounded-lg hover:bg-cmic-50 transition-colors">
              <Pencil size={13} /> Editar
            </button>
          )}
          {canDelete && (
            <button onClick={() => onDelete(post.id)} title="Eliminar" data-testid={`forum-post-delete-${post.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-rose-600 px-2.5 py-1.5 rounded-lg hover:bg-rose-50 transition-colors">
              <Trash2 size={13} /> Eliminar
            </button>
          )}
          {onReply && (
            <button onClick={() => onReply(post)} data-testid={`forum-post-reply-${post.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-cmic-600 hover:text-cmic-700 px-2.5 py-1.5 rounded-lg hover:bg-cmic-50 transition-colors">
              <Reply size={13} /> Responder
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

export default PostBubble;
